import { Reducer } from 'redux';

import {
  CheckedUncheckedTopicAction,
  DismissErrorAction,
  DismissSuccessAction,
  ResetBucketAction,
  SelectAllTopicsAction,
  SetBulkOptionAction,
  UnselectAllTopicsAction,
} from '../../domains/checkedTopicsFromDiscoveryTopics/actions.types';
import { DeleteTopicsAction } from '../../domains/checkedTopicsFromDiscoveryTopics/deleteTopicsActions.types';
import { reportUsage } from '../../telemetry';
import { Topic } from '../types';

import { CheckedTopicsFromDiscoveryTopics } from './types';

export type CheckedTopicsFromDiscoveryTopicsAction =
  | CheckedUncheckedTopicAction
  | SelectAllTopicsAction
  | UnselectAllTopicsAction
  | DismissErrorAction
  | DismissSuccessAction
  | ResetBucketAction
  | DeleteTopicsAction
  | SetBulkOptionAction;

const initialState: CheckedTopicsFromDiscoveryTopics = {
  topicIds: [],
  isSelectedAll: false,
  hasError: false,
  succesfullyProcessTopics: 0,
  isLoading: false,
  activeBulkOption: undefined,
};

const checkedUncheckedTopic = (topicId: Topic['id'], state: CheckedTopicsFromDiscoveryTopics) => {
  const { topicIds } = state;
  const isTopicChecked = topicIds.includes(topicId);

  const updatedTopicIds = isTopicChecked
    ? topicIds.filter((checkedTopicId) => checkedTopicId !== topicId)
    : [...topicIds, topicId];

  const isSelectedAll = isTopicChecked ? false : state.isSelectedAll;

  return { ...state, topicIds: updatedTopicIds, isSelectedAll, hasError: false, activeBulkOption: undefined };
};

const reportBulkDeletionCompleted = (topicsDeletedCount: number, topicsUnableToDeleteCount: number) => {
  reportUsage({
    eventName: 'topic_bulk_deletion_completed',
    eventProperties: {
      topicsDeletedCount,
      topicsUnableToDeleteCount,
    },
  });
};

const removeTopics = (topicIds: Topic['id'][], state: CheckedTopicsFromDiscoveryTopics) => {
  if (topicIds.length === state.topicIds.length) {
    reportBulkDeletionCompleted(topicIds.length, 0);

    return { ...initialState, succesfullyProcessTopics: topicIds.length, activeBulkOption: state.activeBulkOption };
  }

  const filterTopicIds = state.topicIds.filter((topicId) => !topicIds.includes(topicId));
  const succesfullyProcessTopics = state.topicIds.length - topicIds.length;

  reportBulkDeletionCompleted(succesfullyProcessTopics, filterTopicIds.length);

  return {
    topicIds: filterTopicIds,
    isSelectedAll: false,
    hasError: true,
    succesfullyProcessTopics,
    isLoading: false,
    activeBulkOption: state.activeBulkOption,
  };
};

const reducer: Reducer<CheckedTopicsFromDiscoveryTopics, CheckedTopicsFromDiscoveryTopicsAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case 'CHECKED_TOPICS_FROM_DISCOVERY_TOPICS.CHECKED_UNCHECKED_TOPIC':
      return checkedUncheckedTopic(action.payload.topicId, state);
    case 'CHECKED_TOPICS_FROM_DISCOVERY_TOPICS.SELECT_ALL':
      return { ...state, topicIds: [...action.payload.topicIds], isSelectedAll: true, hasError: false };
    case 'CHECKED_TOPICS_FROM_DISCOVERY_TOPICS.UNSELECT_ALL':
      return initialState;
    case 'CHECKED_TOPICS_FROM_DISCOVERY_TOPICS.DISMISS_ERROR':
      return { ...state, hasError: false };
    case 'CHECKED_TOPICS_FROM_DISCOVERY_TOPICS.DISMISS_SUCCESS':
      return { ...state, succesfullyProcessTopics: 0 };
    case 'CHECKED_TOPICS_FROM_DISCOVERY_TOPICS.RESET_TO_INITIAL_STATE':
      return initialState;
    case 'CHECKED_TOPICS_FROM_DISCOVERY_TOPICS.DELETE_TOPICS_PENDING':
      return { ...state, isLoading: true };
    case 'CHECKED_TOPICS_FROM_DISCOVERY_TOPICS.DELETE_TOPICS_FULFILLED':
    case 'CHECKED_TOPICS_FROM_DISCOVERY_TOPICS.DELETE_TOPICS_REJECTED':
      return removeTopics(action.payload.topicIds, state);
    case 'CHECKED_TOPICS_FROM_DISCOVERY_TOPICS.SET_BULK_OPTION':
      return { ...state, activeBulkOption: action.payload.activeBulkOption };
    default:
      return state;
  }
};

export default reducer;
